import React, { Fragment } from 'react'
import PageSeo from '../seo/PageSeo'
import { pageBrowserTitle } from '../../constants/siteMeta'
import { Link } from 'react-router-dom'
import ThemeMenuThree from '../header/ThemeMenuThree'
import PageTitle from '../page-title/PageTitle'
import CommonCounter from '../counter/CommonCounter'
import ProjectMasonryTwo from './ProjectMasonryTwo'
import ProjectDetailsPreviewSlider from './ProjectDetailsPreviewSlider'
import ServiceCategory from '../widget/ServiceCategory'
import WidgetForm from '../form/WidgetForm'
import CaseSliderOne, { getCaseSliderItemsForDetailSlug } from '../case-study/CaseSliderOne'
import NewsletterTwo from '../call-to-action/NewsletterTwo'
import FooterThree from '../footer/FooterThree'

/**
 * @typedef {{
 *   meta: { title: string, description: string },
 *   pageTitle: { title: string, parentTo: string, parentLabel: string, listTo?: string },
 *   hero: { title: string, lead: string },
 *   info: { left: [string, string][], right: [string, string][], tags: string },
 *   introParagraph: string,
 *   challenge: { body: string, bullets: string[] },
 *   previewSlides: { src: string, alt: string }[],
 *   goals: { body: string, counters: { value: string, suffix: string, label: string }[] },
 *   solution: { body: string },
 *   gallery: { images: string[], alts: string[], itemClassNames?: string[] },
 *   sidebar: { category: string, icon: string, routerPath: string }[],
 *   infoBackgroundImage?: string,
 *   liveSiteUrl?: string,
 *   liveSiteLabel?: string,
 * }} CaseStudyShape
 */

/** 밝은 스크린샷용: 기본 flc-rwa 배경과 유사한 다크 톤(과제 영역 그라데이션과 동계열) */
const PROJECT_INFO_BG_SCRIM =
    'linear-gradient(180deg, rgba(5, 13, 54, 0.78) 0%, rgba(5, 13, 54, 0.92) 100%)'

/**
 * @param {{ study: CaseStudyShape, detailSlug?: string | null }} props
 */
const ProjectCaseStudyLayout = ({ study, detailSlug = null }) => {
    const {
        meta,
        pageTitle,
        hero,
        info,
        introParagraph,
        challenge,
        previewSlides,
        goals,
        solution,
        gallery,
        sidebar,
        infoBackgroundImage,
        liveSiteUrl,
        liveSiteLabel,
    } = study

    const relatedCaseSliderItems = getCaseSliderItemsForDetailSlug(detailSlug)

    const hasCustomInfoBg = infoBackgroundImage != null && infoBackgroundImage !== ''
    const projectInfoStyle = hasCustomInfoBg
        ? {
              backgroundImage: `${PROJECT_INFO_BG_SCRIM}, url(${infoBackgroundImage})`,
          }
        : undefined

    return (
        <Fragment>
            <div className="main-page-wrapper">
                <PageSeo
                    title={pageBrowserTitle(pageTitle.title)}
                    description={meta.description}
                    pathname={
                        detailSlug ? `/project-details/${detailSlug}` : '/project-details'
                    }
                    image={infoBackgroundImage || gallery?.images?.[0]}
                />

                <ThemeMenuThree />

                <PageTitle title={pageTitle.title} parentTo={pageTitle.parentTo} parentLabel={pageTitle.parentLabel} />

                <section className="project-details-wrapper pt-145 pb-30 pt-lg-60 pb-lg-20">
                    <div className="container">
                        <div className="row gx-4 gx-xxl-5 align-items-center justify-content-center">
                            <div className="col-lg-12">
                                <div className="project-details-content-top mb-50">
                                    <h2 className="sect-title mb-20">{hero.title}</h2>
                                    <p className="text-white mb-35">{hero.lead}</p>
                                    {liveSiteUrl ? (
                                        <p className="mb-35">
                                            <a
                                                className="theme_btn theme_btn--outline"
                                                href={liveSiteUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                실제 서비스 보기 — {liveSiteLabel || liveSiteUrl.replace(/^https?:\/\//, '')}
                                            </a>
                                        </p>
                                    ) : null}
                                </div>
                                <div
                                    className={`project-info d-flex justify-content-end mb-60${
                                        hasCustomInfoBg ? ' project-info--custom-bg' : ''
                                    }`}
                                    style={projectInfoStyle}
                                >
                                    <div className="info-box">
                                        <h4 className="semi-title mb-20">프로젝트 정보</h4>
                                        <div className="box d-md-flex align-items-center">
                                            <ul className="list-none pe-5">
                                                {info.left.map(([label, value]) => (
                                                    <li key={label}>
                                                        <div className="info-list">
                                                            <span>{label}</span>
                                                            <h5>{value}</h5>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                            <ul className="list-none">
                                                {info.right.map(([label, value]) => (
                                                    <li key={label}>
                                                        <div className="info-list">
                                                            <span>{label}</span>
                                                            <h5>{value}</h5>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="info-list">
                                            <span>태그</span>
                                            <h5>{info.tags}</h5>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row gx-4 gx-xxl-5">
                            <div className="col-lg-8">
                                <div className="widget-left-section">
                                    <p className="text-white mb-55">{introParagraph}</p>
                                    <hr />
                                    <h3 className="sect-title-two fw-bold mb-20 mt-55">과제</h3>
                                    <p className="text-white mb-35">{challenge.body}</p>
                                    <ul className="text-list list-none">
                                        {challenge.bullets.map((line) => (
                                            <li key={line}>{line}</li>
                                        ))}
                                    </ul>
                                    <ProjectDetailsPreviewSlider slides={previewSlides} />
                                    <hr />
                                    <h3 className="sect-title-two fw-bold mb-20 mt-55">목표</h3>
                                    <p className="text-white">{goals.body}</p>
                                    <div className="row gx-4 gx-xxl-5 mt-40">
                                        {goals.counters.map((c, idx) => (
                                            <div key={`${c.label}-${idx}`} className="col-md-3 col-sm-6" data-aos="fade-up">
                                                <div className="counter-style-two boder-style mb-45">
                                                    <h3>
                                                        <span className="counter">
                                                            <CommonCounter value={c.value} />
                                                        </span>
                                                        {c.suffix}
                                                    </h3>
                                                    <p className="text-white mt-20">{c.label}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <hr className="mt-10 mb-55" />
                                    <h3 className="sect-title-two fw-bold mb-20">솔루션</h3>
                                    <p className="text-white mb-40">{solution.body}</p>
                                    <ProjectMasonryTwo
                                        masonryClassName="project-masonry-two--uniform-height"
                                        images={gallery.images}
                                        imageAlts={gallery.alts}
                                        gridItemClassNames={gallery.itemClassNames}
                                        enableLightbox={detailSlug === 'digital-assets-exchange-platform'}
                                    />
                                    <div className="text-center mt-45 mt-lg-55 mb-10">
                                        <Link className="theme_btn" to={pageTitle.listTo ?? pageTitle.parentTo}>
                                            목록으로 가기
                                        </Link>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-4">
                                <div className="widget-right-section">
                                    <ServiceCategory items={sidebar} />
                                    <div className="widget-form mt-60">
                                        <h3 className="widget-title mb-20">카카오톡 채널 채팅 문의하기</h3>
                                        <p className="mb-40">
                                            유사한 플랫폼·랜딩 구축 문의는 카카오톡 채널 채팅으로 연결해 주시면 담당자가 안내해
                                            드립니다.
                                        </p>
                                        <WidgetForm />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="latest-case-work slick-nav blog-slick-two pt-75 pb-150 pt-lg-65">
                    <img className="case-shape shape-1b" src="/assets/img/shape/star-3b.svg" alt="" />
                    <img className="case-shape shape-2b" src="/assets/img/shape/star-4b.svg" alt="" />
                    <img className="case-shape shape-3b d-none d-lg-inline-block" src="/assets/img/shape/star-2.svg" alt="" />
                    <img className="case-shape shape-4b" src="/assets/img/shape/star-5b.svg" alt="" />
                    <div className="container">
                        <div className="row gx-4 gx-xxl-5 align-items-center">
                            <div className="col-xl-5 col-lg-6 col-md-8">
                                <div className="section-title mb-55 text-md-start text-center">
                                    <h3 className="sect-title mb-25" data-aos="fade-up" data-aos-delay={100}>
                                        다른 구축 사례
                                    </h3>
                                </div>
                            </div>
                        </div>
                        <CaseSliderOne items={relatedCaseSliderItems ?? undefined} />
                        <div className="row">
                            <div className="col-lg-12 text-center mt-25 mt-lg-35 mb-0">
                                <Link className="theme_btn" to="/project-grid">
                                    목록으로 보기
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                <NewsletterTwo />

                <FooterThree />
            </div>
        </Fragment>
    )
}

export default ProjectCaseStudyLayout
